import { useCallback, useEffect, useState } from "react";
import { ClientOverviewRepositoryImpl } from "../../data/repositories/ClientOverviewRepositoryImpl";
import { GetExpenseThisMonthInsightsUseCase } from "../../domain/use-cases/GetExpenseThisMonthInsightsUseCase";
import type { ExpenseThisMonthInsightsPanelProps } from "../components/expense_components/ExpenseThisMonthInsightsPanel";

const repository = new ClientOverviewRepositoryImpl();
const getExpenseThisMonthInsightsUseCase = new GetExpenseThisMonthInsightsUseCase(repository);

export interface UseExpenseThisMonthInsightsOptions {
  enabled?: boolean;
}

const defaultInsights: ExpenseThisMonthInsightsPanelProps = {
  monthLabel: "— · Insights",
  insights: [
    { title: "SPEND PACE", description: "—", accentColor: "neutral" },
    { title: "BILLS LOGGED", description: "—", accentColor: "neutral" },
  ],
  loading: false,
};

export function useExpenseThisMonthInsights(
  options?: UseExpenseThisMonthInsightsOptions,
): ExpenseThisMonthInsightsPanelProps & { error: string | null } {
  const enabled = options?.enabled ?? true;

  const [data, setData] = useState<ExpenseThisMonthInsightsPanelProps>(defaultInsights);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInsights = useCallback(async () => {
    if (!enabled) {
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const entity = await getExpenseThisMonthInsightsUseCase.execute();
      setData({
        monthLabel: entity.monthLabel,
        insights: entity.insights.slice(0, 2),
        loading: false,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load expense insights",
      );
      setData(defaultInsights);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchInsights();
  }, [enabled, fetchInsights]);

  return { ...data, loading, error };
}

